import { KeywordContext } from "@app/types/Page";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import { KeywordButtonType } from "@components/Button/utils/types";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import GAData from "@components/GAData/GAData";
import Header from "@components/Header/Header";
import KeywordFilter from "@components/KeywordFilter";
import { KeywordType } from "@components/KeywordFilter/utils/types";
import { renderMetaDescription } from "@components/MetaTags/helpers";
import MetaTags from "@components/MetaTags/MetaTags";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useMostReadArticleList } from "@hooks/useMostReadArticleList";
import { useTrimmedBreakingNews } from "@hooks/useTrimmedBreakingNews";
import { getUrlPathFromAbsoluteUrl } from "@pages/Article/utils/helpers";
import AsiaFutureSummitLogo from "@pages/Keyword/templates/TemplateKeywordDefault/components/AsiaFutureSummitLogo";
import BreakingNews from "@pages/Section/components/BreakingNews/BreakingNews";
import MostPopular from "@pages/Section/components/MostPopular/MostPopular";
import { getFormattedTextForAds } from "@pages/Section/helpers";
import Listing from "@particles/listing/Listing";
import { Tag } from "@typings/Cue";
import { ASIA_FUTURE_SUMMIT_URL, sectionNavigationItems } from "@util/constant";
import { cn } from "@util/helpers";
import { useDetectAdBlock } from "adblock-detect-react";

type TemplateKeywordDefaultProps = Pick<KeywordContext, "data">;

/**
 * Default template for Keyword pages.
 *
 * @param {TemplateKeywordDefaultProps} props - The properties for the template.
 * @returns {React.ReactElement} The rendered Keyword default template component.
 */
const TemplateKeywordDefault = ({
  data: { title, entity, overview, seoFooterData },
}: TemplateKeywordDefaultProps): React.ReactElement => {
  const breakingNews = useTrimmedBreakingNews();
  const mostPopular = useMostReadArticleList();
  const adBlockDetected = useDetectAdBlock();

  const {
    tag: { name, urlPath, id, type },
    urlPathHistory,
  } = entity;

  const relativePath = getUrlPathFromAbsoluteUrl(urlPath);

  // @todo comment to changed this to FeedTag once myBT integration is done
  const legacyTag: Tag = {
    id,
    name: name,
    urlPath: urlPath,
    type: type,
    uri: "",
    urlPathHistory: urlPathHistory,
    aliases: [],
    fields: [],
    parent: null,
    sections: [],
  };

  return (
    <div className="w-full" data-testid="keyword-page-content-component">
      <AdSettings
        adNames={["lb1", "imu2", "prestitial", "catfish", "abm"]}
        path={relativePath}
        adTargets={[
          { key: "page", value: "listing" },
          { key: "bttags", value: getFormattedTextForAds(name) },
        ]}
      />

      <GAData
        chapter1={name}
        level2={"keywords"}
        title={"keywords"}
        adblocker={adBlockDetected ? 1 : 0}
      />

      <MetaTags
        title={title}
        description={renderMetaDescription({ kind: "keyword", entity: entity })}
        ogType="article"
        slug={relativePath}
      />

      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header
        parentCategory="keywords"
        childCategory={relativePath.replace("/keywords/", "")}
        categoryDataMapping={sectionNavigationItems}
      />

      <PulseEntry />

      <Container rootClassName="pt-6">
        {urlPath === ASIA_FUTURE_SUMMIT_URL ? (
          <AsiaFutureSummitLogo
            className={cn("mb-6 md:border-b md:border-gray-850 md:py-4")}
          />
        ) : (
          <KeywordFilter
            keywordButtonType={KeywordButtonType.version2}
            tag={legacyTag}
            rootClassName={cn(
              "mb-6 py-3 pt-6 inline-block w-full border-b border-gray-850"
            )}
            tagClassName="border-none !font-playfair !text-6xl md:!text-8xl !p-0 text-gray-850 !uppercase !text-left !text-normal"
            toolTipClassName={
              "!right-0 top-full min-[425px]:!left-auto min-[425px]:top-1/2 min-[425px]:flex min-[425px]:-translate-y-1/2 min-[425px]:-translate-x-0 min-[425px]:items-center xl:left-auto xl:top-full xl:block xl:-translate-x-0 xl:translate-y-0 top-full min-[425px]:top-full min-[425px]:!block left-auto w-[200px] xs:w-[250px] xl:translate-x-0"
            }
            toolTipArrowClassName={
              "xl:ml-0 xl:mr-10 md:mr-10 border-b-[8px] border-l-[6px] border-r-[6px] border-x-transparent border-b-black mr-3 min-[425px]:border-r-transparent min-[425px]:!border-b-black min-[425px]:border-t-transparent relative left-[90%] md:left-[80%] mr-0 border-t-transparent ml-0"
            }
            disableTooltip
            keywordType={KeywordType.listing}
            buttonClassName={"lg:ml-4"}
          />
        )}

        <div className="grid lg:grid-cols-12">
          <div className="mb-6 lg:col-span-8">
            <Listing
              cardProps={overview}
              urlPath={urlPath}
              pageType={"keyword"}
              size={10}
            />
          </div>

          <div className="mb-6 lg:col-span-4 lg:pl-6">
            <div className="mb-6 lg:h-[800px]">
              <Ad
                adType="imu2"
                className="flex flex-col items-center justify-center lg:!sticky lg:top-[60px]"
              />
            </div>

            <BreakingNews
              data={breakingNews}
              rootClassName="max-w-[300px] mx-auto mb-6"
            />

            <MostPopular
              data={mostPopular}
              rootClassName="max-w-[300px] mx-auto"
            />
          </div>
        </div>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="abm" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default TemplateKeywordDefault;
